import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"

export interface UserForOrgSlug {
  readonly tenant_code?: string
}

// Premiers segments d'URL qui correspondent à des MODULES d'app (nested sous
// `/:orgSlug/<module>/...` dans App.tsx). Utilisés pour monter des Routes
// fallback (`/<module>/*` → BarePathRedirect) qui interceptent les deep-links
// bare AVANT que `/:orgSlug` ne matche parasitement (React Router v6 : un
// segment litéral bat un segment dynamique).
//
// Toute nouvelle catégorie de nested routes dans `<Route path="/:orgSlug">`
// doit s'ajouter ici. Le test `app-path-prefixes-exhaustivity.test.ts`
// (parse App.tsx et compare) fail sinon.
export const APP_PATH_PREFIXES: readonly string[] = [
  "dashboard",
  "tasks",
  "crm",
  "training",
  "commercial",
  "qualiopi",
  "documents",
  "hr",
  "messages",
  "admin",
  "help",
  "settings",
  "reports",
]

// Préfixes de chemin qui NE font PAS partie de l'espace multi-tenant
// `/:orgSlug/*`. Toute nouvelle route publique (auth, portail, endpoint
// backend servi par nginx, magic-link token) doit s'ajouter ici.
export const PUBLIC_PATH_PREFIXES: readonly string[] = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/accept-invite",
  "/verify-email",
  "/onboarding",
  "/app/",
  "/public/",
  "/jury/",
  "/q/",
  "/qa/",
  "/survey/",
  "/i/",
  "/jd/",
  "/vc/",
  "/verify/",
  "/a/",
  "/formateur/",
  "/portal/",
  "/c/",
  "/api/",
  "/oauth2",
  "/mcp",
  "/.well-known",
]

// True si le pathname (sans query/hash) tombe dans un espace public/non-app.
// Le match exact + suivi de "/" évite qu'un prefix `/login` matche `/loginfoo`.
export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATH_PREFIXES.some((prefix) => {
    if (prefix.endsWith("/")) {
      const stripped = prefix.slice(0, -1)
      return pathname === stripped || pathname.startsWith(prefix)
    }
    return pathname === prefix || pathname.startsWith(`${prefix}/`)
  })
}

// Injecte le slug de l'organisation en préfixe du chemin. Idempotent : si le
// chemin commence déjà par /<slug> ou /<slug>/..., retourne fullPath inchangé.
// Préserve la query string (?...) et le hash (#...) bit-à-bit.
//
// Contrat :
//   - slug non-vide requis. Si slug === "", retourne fullPath inchangé
//     (garde défensive : jamais de résultat `//path` qui pointerait sur
//     un autre host en SPA routing).
//   - fullPath normalisé pour toujours commencer par "/".
export function injectOrgSlug(fullPath: string, slug: string): string {
  if (slug.length === 0) return fullPath

  const searchIdx = fullPath.indexOf("?")
  const hashIdx = fullPath.indexOf("#")
  const pathEnd =
    searchIdx >= 0 && hashIdx >= 0
      ? Math.min(searchIdx, hashIdx)
      : searchIdx >= 0
      ? searchIdx
      : hashIdx >= 0
      ? hashIdx
      : fullPath.length

  const rawPathname = fullPath.slice(0, pathEnd)
  const tail = fullPath.slice(pathEnd)
  const pathname = rawPathname.startsWith("/") ? rawPathname : `/${rawPathname}`

  if (pathname === `/${slug}` || pathname.startsWith(`/${slug}/`)) {
    return `${pathname}${tail}`
  }

  return `/${slug}${pathname}${tail}`
}

// Résout le slug de l'organisation courante depuis les sources persistantes.
// Ordre :
//   1. localStorage.lastOrgSlug (MAJ à chaque login/switch tenant côté client)
//   2. user.tenant_code (JWT hydraté par le backend post-login)
// Retourne O.none si aucune source ne fournit un slug non-vide (état rare :
// backend qui n'aurait pas hydraté tenant_code, ou logout partiel).
export function resolveOrgSlug(user: UserForOrgSlug | null): O.Option<string> {
  const fromStorage = pipe(
    O.fromNullable(localStorage.getItem("lastOrgSlug")),
    O.filter((s) => s.length > 0),
  )
  const fromUser = pipe(
    O.fromNullable(user),
    O.chain((u) => O.fromNullable(u.tenant_code)),
    O.filter((s) => s.length > 0),
  )
  return pipe(
    fromStorage,
    O.alt(() => fromUser),
  )
}
